import { createSlice } from "@reduxjs/toolkit";
import AdminApi from "../../../api/AdminApi";
import { showNotification } from "./notification";

type State = {
	data: Array<Object>;
	isLoading: boolean;
	isUpdating: boolean;
	troubleLoading: boolean;
	error: Object | null;
	timestamp: number;
	isDirty: boolean;
};

const initialState = {
	data: [],
	isLoading: false,
	isUpdating: false,
	troubleLoading: false,
	error: null,
	timestamp: 0,
	isDirty: false,
};

const goals = createSlice({
	name: "goals",
	initialState,
	reducers: {
		fetchGoalsStart: (state: State) => {
			state.isLoading = true;
			state.error = null;
			state.isDirty = false;
		},
		fetchGoalsSuccess: (state: State, action) => {
			if (action.payload && Array.isArray(action.payload)) {
				state.data = action.payload;
			}
			state.troubleLoading = false;
			state.isLoading = false;
			state.error = false;
			state.timestamp = Date.now();
		},
		fetchGoalsTroubleLoading: (state : State, action) => {
			state.isLoading = false;
			state.troubleLoading = true;
		},
		fetchGoalsFailed: (state: State, action) => {
			state.isLoading = false;
			state.error = action.payload;
		},
		saveGoalStart: (state: State) => {
			state.error = null;
			state.isUpdating = true;
		},
		saveGoalSuccess: (state: State, action) => {
			state.error = null;
			state.isUpdating = false;
			state.isLoading = false;
			state.isDirty = true;
		},
		saveGoalFailed: (state: State, action) => {
			state.error = action.payload;
			state.isUpdating = false;
		},
		deleteGoalStart: (state: State) => {
			state.error = null;
			state.isUpdating = true;
		},
		deleteGoalSuccess: (state: State, action) => {
			state.error = null;
			state.isUpdating = false;
			state.isDirty = true;
			state.isLoading = false;
		},
		deleteGoalFailed: (state: State, action) => {
			state.error = action.payload;
			state.isUpdating = false;
		},
		updateGoalStart: (state: State) => {
			state.error = null;
			state.isUpdating = true;
		},
		updateGoalSuccess: (state: State, action) => {
			state.error = null;
			state.isUpdating = false;
			state.isLoading = false;
			state.isDirty = true;
		},
		updateGoalFailed: (state: State, action) => {
			state.error = action.payload;
			state.isUpdating = false;
		},
	},
});

// Actions
export const {
	fetchGoalsStart,
	fetchGoalsSuccess,
	fetchGoalsFailed,
	fetchGoalsTroubleLoading,
	saveGoalStart,
	saveGoalSuccess,
	saveGoalFailed,
	deleteGoalStart,
	deleteGoalSuccess,
	deleteGoalFailed,
	updateGoalStart,
	updateGoalSuccess,
	updateGoalFailed,
} = goals.actions;

export default goals.reducer;

// Thunk
export const fetchGoals = () => async (dispatch: Function) => {
	try {
		dispatch(fetchGoalsStart());
		const api = new AdminApi();
		const result = await api.getGoals();
		if (result === "trouble loading") {
			dispatch(fetchGoalsTroubleLoading(result))
		} else {
			dispatch(fetchGoalsSuccess(result));
		}
	} catch (err: any) {
		dispatch(fetchGoalsFailed(err));
		dispatch(showNotification("error", err.message));
	}
};

export const saveGoal = (data: any) => async (dispatch: Function) => {
	try {
		dispatch(saveGoalStart());
		if (!(data.target > 0)) {
			throw new Error("Target must be a positive integer");
		}
		if (data.recurrencyType === "daily" && data.recurrencyValue === "") {
			throw new Error("Please select at least one day");
		}
		const api = new AdminApi();
		const result = await api.saveGoal(data);
		dispatch(saveGoalSuccess(result));
		dispatch(showNotification("success", "Your new goal has been successfully saved."));
		setTimeout(() => {
			dispatch(fetchGoals());
			window.location.href = "/admin/goals";
		}, 1000);
	} catch (err: any) {
		dispatch(saveGoalFailed(err));
		dispatch(showNotification("error", err.message));
	}
};

export const deleteGoal =
	(id?: string | number) => async (dispatch: Function) => {
		try {
			dispatch(deleteGoalStart());
			const api = new AdminApi();
			const result = await api.deleteGoal(id);
			dispatch(deleteGoalSuccess(result));
			dispatch(showNotification("success", "Goal deleted successfully."));
			setTimeout(() => {
				dispatch(fetchGoals());
			}, 1000);
		} catch (err: any) {
			dispatch(deleteGoalFailed(err));
			dispatch(showNotification("error", err.message));
		}
	};

export const updateGoal =
	(id: string | number, data: any) => async (dispatch: Function) => {
		try {
			dispatch(updateGoalStart());
			if (data.target <= 0 ) {
				throw new Error("Target must be a positive integer")
			}
			const api = new AdminApi();
			const result = await api.updateGoal(id, data);
			dispatch(updateGoalSuccess(result));
			dispatch(
				showNotification(
					"success",
					"Your goal has been successfully updated."
				)
			);
			setTimeout(() => {
				dispatch(fetchGoals());
				window.location.href = "/admin/goals";
			}, 1000);
		} catch (err: any) {
			dispatch(updateGoalFailed(err));
			dispatch(showNotification("error", err.message));
		}
	};
